import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { Download, FileSpreadsheet, Play, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  applyCoaWorkbook,
  downloadCoaWorkbook,
  getCoaEngineRun,
  importCoaAnalysis,
  previewCoaWorkbook,
  runCoaEngine,
  type CoaWorkbookFilters,
  type CoaWorkbookPreview,
} from "@/api/coa-validation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCurrentUser } from "@/hooks/useAuth";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import { hasMinRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

import type { CoaAction } from "@/api/diagnostic-chart-of-accounts";
import { NO_VALUE } from "@/components/projects/diagnostic/sectionState";

import { useCoaActionLabels } from "./coaLabels";

const BUTTON =
  "inline-flex items-center gap-1.5 rounded-md border border-[#d7dde6] bg-white px-3 py-1.5 text-[12px] font-semibold text-[#08254c] hover:bg-[#f5f8fc] disabled:cursor-not-allowed disabled:opacity-50";

function errorDetail(err: unknown): string | null {
  const detail = isAxiosError(err) ? err.response?.data?.detail : undefined;
  if (typeof detail === "string") return detail;
  // Erro com código: `{code, message}`, como a planilha recusada inteira.
  if (detail && typeof detail === "object" && typeof detail.message === "string") {
    return detail.message;
  }
  // Validação do FastAPI: lista de erros, cada um com `msg`.
  const first = Array.isArray(detail) ? detail[0] : undefined;
  return first && typeof first.msg === "string" ? first.msg : null;
}

/**
 * Gerar a análise do plano de contas pela tela (#1374), no detalhe do Diagnóstico.
 *
 * "Rodar o motor" lê a extração do plano de contas da última execução do Functional
 * e o YCOA de referência, e leva perto de dois minutos: a tela acompanha pelo
 * status até terminar. "Importar análise" recebe a planilha no layout da análise
 * de referência. Nos dois casos, a análise nova vira a vigente e o quadro do Ato 1
 * é relido.
 *
 * Para curadoria grande, a revisão em massa no Excel (P8): "Baixar planilha"
 * (todas as contas ou o filtro da tela) e "Carregar planilha alterada", com
 * prévia antes de gravar.
 *
 * Só para consultor: o backend recusa todos para o papel `user`.
 */
export function CoaAnalysisActions(props: {
  projectId: string;
  pollMs?: number;
  /** O filtro do detalhe: a planilha baixada leva as mesmas contas. */
  filters?: CoaWorkbookFilters;
}) {
  // Fora do componente das ações: quem não é consultor não dispara consulta nenhuma.
  const canAct = hasMinRole(useCurrentUser()?.role, "consultant");
  return canAct ? <Actions {...props} /> : null;
}

function Actions({
  projectId,
  pollMs = 3000,
  filters = {},
}: {
  projectId: string;
  pollMs?: number;
  filters?: CoaWorkbookFilters;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [importMessage, setImportMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const runKey = [...diagnosticKeys.snapshot(projectId), "coa-engine-run"] as const;
  const run = useQuery({
    queryKey: runKey,
    queryFn: () => getCoaEngineRun(projectId),
    refetchInterval: (query) => (query.state.data?.status === "running" ? pollMs : false),
  });
  const status = run.data?.status;

  // A análise nova vira a vigente: o quadro do Ato 1 e a lista são relidos.
  const lastStatus = useRef(status);
  useEffect(() => {
    if (lastStatus.current === "running" && status === "done") {
      void queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    }
    lastStatus.current = status;
  }, [status, projectId, queryClient]);

  const start = useMutation({
    mutationFn: () => runCoaEngine(projectId),
    onSuccess: (data) => queryClient.setQueryData(runKey, data),
    onSettled: () => queryClient.invalidateQueries({ queryKey: runKey }),
  });

  const upload = useMutation({
    mutationFn: (file: File) => importCoaAnalysis(projectId, file),
    onSuccess: (summary) => {
      setImportMessage({
        ok: true,
        text: t("Analysis imported: {{count}} accounts.", {
          count: formatCount(summary.account_count),
        }),
      });
      void queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    },
    onError: (err) =>
      setImportMessage({ ok: false, text: errorDetail(err) ?? t("Could not import the analysis.") }),
  });

  const running = status === "running" || start.isPending;
  const startError = start.isError ? errorDetail(start.error) : null;

  return (
    <div
      data-testid="coa-analysis-actions"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-[#e8ebf0] bg-[#f7f9fc] px-4 py-3"
    >
      <button
        type="button"
        data-testid="coa-run-engine"
        disabled={running || upload.isPending}
        onClick={() => start.mutate()}
        className={BUTTON}
      >
        <Play aria-hidden className="h-3.5 w-3.5" />
        {t("Run the adherence engine")}
      </button>

      <label className={`${BUTTON} cursor-pointer`}>
        <Upload aria-hidden className="h-3.5 w-3.5" />
        {t("Import analysis (spreadsheet)")}
        <input
          ref={fileInput}
          data-testid="coa-import-file"
          type="file"
          accept=".xlsx"
          className="sr-only"
          disabled={running || upload.isPending}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setImportMessage(null);
              upload.mutate(file);
            }
            if (fileInput.current) fileInput.current.value = "";
          }}
        />
      </label>

      <span data-testid="coa-run-status" className="text-[12px] text-[#596b85]">
        {running
          ? t("Running the engine: about two minutes.")
          : status === "done"
            ? t("Analysis updated.")
            : status === "failed"
              ? run.data?.detail || t("The engine failed.")
              : startError ?? null}
      </span>

      {upload.isPending && (
        <span className="text-[12px] text-[#596b85]">{t("Importing the analysis…")}</span>
      )}
      {importMessage && (
        <span
          data-testid="coa-import-status"
          className={importMessage.ok ? "text-[12px] text-[#2e6b45]" : "text-[12px] text-[#a23b2f]"}
        >
          {importMessage.text}
        </span>
      )}

      <WorkbookActions projectId={projectId} filters={filters} disabled={running || upload.isPending} />
    </div>
  );
}

/**
 * Revisão em massa no Excel (P8): baixar a planilha protegida e carregar a
 * planilha alterada, com prévia. Nada é gravado antes de "Aplicar".
 */
function WorkbookActions({
  projectId,
  filters,
  disabled,
}: {
  projectId: string;
  filters: CoaWorkbookFilters;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = useState<{ file: File; preview: CoaWorkbookPreview } | null>(null);
  const [useFile, setUseFile] = useState<Record<string, boolean>>({});

  const download = useMutation({
    mutationFn: () => downloadCoaWorkbook(projectId, filters),
    onError: (err) =>
      setMessage({ ok: false, text: errorDetail(err) ?? t("Could not download the spreadsheet.") }),
  });

  const preview = useMutation({
    mutationFn: (file: File) => previewCoaWorkbook(projectId, file).then((data) => ({ file, data })),
    onSuccess: ({ file, data }) => {
      setUseFile({});
      setPending({ file, preview: data });
    },
    onError: (err) =>
      setMessage({ ok: false, text: errorDetail(err) ?? t("Could not read the spreadsheet.") }),
  });

  const apply = useMutation({
    mutationFn: () => {
      const choices = Object.fromEntries(
        Object.entries(useFile)
          .filter(([, chosen]) => chosen)
          .map(([account]) => [account, "file" as const]),
      );
      return applyCoaWorkbook(projectId, pending!.file, choices);
    },
    onSuccess: (result) => {
      setPending(null);
      setMessage({
        ok: true,
        text: `${tp(
          t,
          result.saved,
          "Spreadsheet applied: 1 decision saved",
          "Spreadsheet applied: {{n}} decisions saved",
          { n: formatCount(result.saved) },
        )}, ${tp(t, result.rejected, "1 row rejected.", "{{n}} rows rejected.", {
          n: formatCount(result.rejected),
        })}`,
      });
      void queryClient.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId) });
    },
    onError: (err) =>
      setMessage({ ok: false, text: errorDetail(err) ?? t("Could not apply the spreadsheet.") }),
  });

  const busy = disabled || download.isPending || preview.isPending || apply.isPending;
  const data = pending?.preview;

  return (
    <>
      <button
        type="button"
        data-testid="coa-workbook-download"
        disabled={busy}
        onClick={() => {
          setMessage(null);
          download.mutate();
        }}
        className={BUTTON}
      >
        <Download aria-hidden className="h-3.5 w-3.5" />
        {t("Download curation spreadsheet")}
      </button>

      <label className={`${BUTTON} cursor-pointer`}>
        <FileSpreadsheet aria-hidden className="h-3.5 w-3.5" />
        {t("Upload edited spreadsheet")}
        <input
          ref={fileInput}
          data-testid="coa-workbook-file"
          type="file"
          accept=".xlsx"
          className="sr-only"
          disabled={busy}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) {
              setMessage(null);
              preview.mutate(file);
            }
            if (fileInput.current) fileInput.current.value = "";
          }}
        />
      </label>

      {preview.isPending && (
        <span className="text-[12px] text-[#596b85]">{t("Reading the spreadsheet…")}</span>
      )}
      {message && (
        <span
          data-testid="coa-workbook-status"
          className={message.ok ? "text-[12px] text-[#2e6b45]" : "text-[12px] text-[#a23b2f]"}
        >
          {message.text}
        </span>
      )}

      <Dialog open={!!data} onOpenChange={(open) => !open && setPending(null)}>
        <DialogContent data-testid="coa-workbook-preview" className="max-h-[85vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("Review before applying")}</DialogTitle>
            <DialogDescription>
              {t(
                "Nothing is saved until you apply. In a conflict the platform value is kept unless you choose the spreadsheet.",
              )}
            </DialogDescription>
          </DialogHeader>
          {data && (
            <div className="space-y-4 text-[12px] text-[#08254c]">
              <dl className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                <PreviewCount testId="coa-preview-read" label={t("Rows read")} value={data.read} />
                <PreviewCount testId="coa-preview-changed" label={t("Rows changed")} value={data.changed} />
                <PreviewCount testId="coa-preview-unchanged" label={t("Rows unchanged")} value={data.unchanged} />
                <PreviewCount testId="coa-preview-conflicts" label={t("Conflicts")} value={data.conflicts_count} />
                <PreviewCount testId="coa-preview-rejected" label={t("Rejected rows")} value={data.rejected_count} />
                <PreviewCount testId="coa-preview-locked" label={t("Locked fields ignored")} value={data.locked_ignored} />
              </dl>

              {data.conflicts.length > 0 && (
                <section>
                  <h4 className="mb-1 font-semibold">{t("Conflicts: changed on the platform after the download")}</h4>
                  <ul className="space-y-1">
                    {data.conflicts.map((conflict) => {
                      const account = String(conflict.account);
                      const platform = (conflict.platform ?? {}) as DecisionValues;
                      const sheet = (conflict.file ?? {}) as DecisionValues;
                      return (
                        <li key={account} className="rounded-md border border-[#e8ebf0] bg-white px-2 py-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono font-semibold">{account}</span>
                            <label className="inline-flex items-center gap-1">
                              <input
                                type="checkbox"
                                data-testid={`coa-conflict-file-${account}`}
                                checked={!!useFile[account]}
                                onChange={(event) =>
                                  setUseFile((current) => ({ ...current, [account]: event.target.checked }))
                                }
                              />
                              {t("Use the spreadsheet value")}
                            </label>
                          </div>
                          <ConflictValues
                            testId={`coa-conflict-platform-${account}`}
                            label={t("Platform")}
                            values={platform}
                            other={sheet}
                          />
                          <ConflictValues
                            testId={`coa-conflict-sheet-${account}`}
                            label={t("Spreadsheet")}
                            values={sheet}
                            other={platform}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}

              {data.rejected.length > 0 && (
                <section>
                  <h4 className="mb-1 font-semibold">{t("Rejected rows")}</h4>
                  <ul className="space-y-0.5">
                    {data.rejected.map((row) => (
                      <li key={`${row.row}-${row.account}`}>
                        {t("Row {{row}}", { row: row.row })} · <span className="font-mono">{String(row.account)}</span>{" "}
                        · {String(row.reason)}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
          )}
          <DialogFooter>
            <button type="button" className={BUTTON} onClick={() => setPending(null)}>
              {t("Cancel")}
            </button>
            <button
              type="button"
              data-testid="coa-workbook-apply"
              disabled={apply.isPending}
              onClick={() => apply.mutate()}
              className={BUTTON}
            >
              <Upload aria-hidden className="h-3.5 w-3.5" />
              {t("Apply")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Os campos de uma decisão como o backend devolve no conflito da planilha. */
interface DecisionValues {
  decision?: string | null;
  mirror_account?: string | null;
  final_account?: string | null;
  note?: string | null;
}

/**
 * Um lado do conflito (plataforma ou planilha), com o que difere do outro lado
 * em destaque: o consultor escolhe vendo o que está escolhendo.
 */
function ConflictValues({
  testId,
  label,
  values,
  other,
}: {
  testId: string;
  label: string;
  values: DecisionValues;
  other: DecisionValues;
}) {
  const { t } = useTranslation();
  const actions = useCoaActionLabels();
  const parts: [keyof DecisionValues, string | null][] = [
    ["decision", values.decision ? (actions[values.decision as CoaAction] ?? values.decision) : NO_VALUE],
    ["mirror_account", `${t("Mirror account")} ${values.mirror_account ?? NO_VALUE}`],
    ["final_account", `${t("Final account in YCOA")} ${values.final_account ?? NO_VALUE}`],
    ["note", values.note ? `"${values.note}"` : null],
  ];
  return (
    <div data-testid={testId} className="mt-0.5 text-[11px] text-[#596b85]">
      <span className="text-[#7b8799]">{label}:</span>{" "}
      {parts
        .filter(([, text]) => text)
        .map(([field, text], index) => (
          <span key={field}>
            {index > 0 && " · "}
            <span className={cn((values[field] ?? null) !== (other[field] ?? null) && "font-semibold text-[#08254c]")}>
              {text}
            </span>
          </span>
        ))}
    </div>
  );
}

function PreviewCount({ testId, label, value }: { testId: string; label: string; value: number }) {
  return (
    <div data-testid={testId} className="rounded-md border border-[#e8ebf0] bg-white px-2 py-1.5">
      <dt className="text-[10px] uppercase tracking-wide text-[#7b8799]">{label}</dt>
      <dd className="text-[16px] font-semibold tabular-nums">{formatCount(value)}</dd>
    </div>
  );
}
